import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/** The export formats offered in the dialog. */
export type ExportFormat = 'gsheet' | 'csv' | 'gdoc' | 'pdf';

/** A single active-filter chip shown in the dialog's filter strip. */
export interface ExportFilterChip {
  /** Human label for the filter dimension, e.g. "Status". */
  key: string;
  /** The selected value, e.g. "Completed". */
  value: string;
}

/** Presentational descriptor for one format option button. */
interface ExportOption {
  id: ExportFormat;
  label: string;
  description: string;
  /** Glyph-tile background color (brand color per the design). */
  background: string;
  /** Glyph-tile foreground color. */
  foreground: string;
  /** Which glyph to render in the tile. */
  glyph: 'sheet' | 'doc' | 'csv' | 'pdf';
}

/**
 * Centered modal for exporting the current feedback list. Presentational: it
 * shows the available formats and how many items/filters apply, and emits the
 * chosen format to the host — it does NOT perform the export itself. The host
 * decides what each format does.
 *
 * @example
 * <fb-export-feedback
 *   [open]="exportOpen()"
 *   [visibleCount]="feedbackTotal()"
 *   [totalCount]="feedbackTotal()"
 *   [filterChips]="exportFilterChips()"
 *   (exportFormat)="onExportFormat($event)"
 *   (close)="exportOpen.set(false)" />
 */
@Component({
  selector: 'fb-export-feedback',
  templateUrl: './export-feedback.html',
  styleUrl: './export-feedback.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onClose()' },
})
export class ExportFeedback {
  /** Whether the dialog is shown. */
  readonly open = input<boolean>(false);

  /** Number of items that will be exported (those matching the current filters). */
  readonly visibleCount = input<number>(0);

  /** Total number of items in the round, regardless of filters. */
  readonly totalCount = input<number>(0);

  /** Active filters, shown as chips; an empty list hides the filter strip. */
  readonly filterChips = input<ExportFilterChip[]>([]);

  /** Emitted with the chosen format when the user picks one. */
  readonly exportFormat = output<ExportFormat>();

  /** Emitted when the user dismisses the dialog (button, overlay, or Escape). */
  readonly close = output<void>();

  /** The format options, in display order (PDF spans the full width). */
  protected readonly options: ExportOption[] = [
    {
      id: 'gsheet',
      label: 'Google Sheets',
      description: 'Open as a new spreadsheet',
      background: '#e8f5ec',
      foreground: '#0f9d58',
      glyph: 'sheet',
    },
    {
      id: 'csv',
      label: 'CSV',
      description: 'Plain comma-separated values',
      background: '#f0f1f5',
      foreground: '#43506b',
      glyph: 'csv',
    },
    {
      id: 'gdoc',
      label: 'Google Docs',
      description: 'Open as a formatted document',
      background: '#e6effb',
      foreground: '#1967d2',
      glyph: 'doc',
    },
    {
      id: 'pdf',
      label: 'PDF',
      description: 'Print-ready document',
      background: '#fdecec',
      foreground: '#d93025',
      glyph: 'pdf',
    },
  ];

  /** Uppercased text for the text-glyph tiles (CSV/PDF). */
  protected glyphText(glyph: ExportOption['glyph']): string {
    return glyph.toUpperCase();
  }

  protected onSelect(format: ExportFormat): void {
    if (this.visibleCount() <= 0) {
      return;
    }
    this.exportFormat.emit(format);
  }

  protected onClose(): void {
    if (!this.open()) {
      return;
    }
    this.close.emit();
  }
}
